<template>
    <div class="o-sms-modal sms-modal">
        <component
            v-model="tel"
            :is="defineStatus"
            :is-valid="isValid"
            :item="t(item, 'name')"
            @onChange="handleChange"
        />
        <div class="sms-modal__footer">
            <AButton
                theme="secondary"
                :text="isSuccess"
                class="sms-modal__button sms-modal__button--short"
                @onClick="closeModal"
            >
                <template #left>
                    <svg-icon
                        original
                        name="icons/close-white"
                        class="sms-modal__close-icon"
                    />
                </template>
            </AButton>
            <AButton
                theme="primary"
                :text="isResend"
                class="sms-modal__button"
                :loading="loading"
                :disabled="isDisabledSendSms"
                @onClick="handleSendSms"
            >
                <template #right>
                    <svg-icon
                        original
                        name="icons/send"
                        class="sms-modal__send-icon"
                    />
                </template>
            </AButton>
        </div>
        <div
            v-if="isQr"
            class="send-sms__qr-block"
        >
            <div class="send-sms__bubble">
                <div class="send-sms__bubble-wrapper">
                    <div class="send-sms__bubble-left-shape" />
                    <div class="send-sms__bubble-center">
                        <span
                            class="send-sms__bubble-text"
                            v-html="$t('o-sms-modal_bubble-text')"
                        />
                    </div>
                    <div class="send-sms__bubble-right-shape" />
                </div>
                <div class="send-sms__bubble-bottom" />
            </div>
            <div class="send-sms__qr-container">
                <component :is="defineGenerator"/>
            </div>
        </div>
        <div class="send-sms__close-block">
            <button
                type="button"
                class="send-sms__close-button"
                @click.capture="closeModal"
                :aria-label="ariaLabel"
            >
                <svg-icon
                    original
                    name="icons/close"
                    class="send-sms__close-icon"
                />
            </button>
            <span class="send-sms__close-label">
                {{ $t('ui.close') }}
            </span>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import AButton from '../../atoms/a-button/a-button';
import OWQrGenerator from '../../organisms/o-w-qr-generator/o-w-qr-generator';
import OQrGenerator from '../../organisms/o-qr-code-generator/o-qr-code-generator';
import OSmsModalFail from '../../unique-organisms/o-sms-modal-fail/o-sms-modal-fail';
import OSmsModalReady from '../../unique-organisms/o-sms-modal-ready/o-sms-modal-ready';
import OSmsModalSuccess from '../../unique-organisms/o-sms-modal-success/o-sms-modal-success';

export default {
    name: 'OSmsModal',
    components: {
        AButton,
        OQrGenerator,
        OSmsModalFail,
        OWQrGenerator,
        OSmsModalReady,
        OSmsModalSuccess
    },
    props: {
        ariaLabel: {
            type: String,
            default: 'Send SMS button close'
        }
    },
    data() {
        return {
            tel: '',
            status: 'ready',
            components: {
                ready: 'OSmsModalReady',
                fail: 'OSmsModalFail',
                success: 'OSmsModalSuccess'
            },
            isValid: true,
            loading: false,
            qrGenerators: {
                terminal: 'OQrGenerator',
                web: 'OWQrGenerator'
            }
        }
    },
    computed: {
        ...mapGetters([
            't',
            'getObject',
            'getTerminalId',
            'getDenormalisedObjectById'
        ]),
        item() {
            return this.getDenormalisedObjectById(this.itemId);
        },
        defineStatus() {
            return this.components[this.status];
        },
        isDisabledSendSms() {
            return !!(!this.tel);
        },
        isResend() {
            return ['success', 'fail'].includes(this.status) ? this.$t('ui.resend-sms') : this.$t('ui.send-sms');
        },
        isSuccess() {
            return ['success'].includes(this.status) ? this.$t('ui.close') : this.$t('ui.cancel');
        },
        isQr() {
            return ['ready', 'fail'].includes(this.status);
        },
        defineGenerator() {
            return this.$route.name.includes('terminal') ? this.qrGenerators.terminal : this.qrGenerators.web
        },
        itemId() {
            return this.$route.query.to ? this.$route.query.to : this.$route.params.id;
        }
    },
    watch: {
        tel() {
            this.isValid = true;
        }
    },
    methods: {
        ...mapActions([
            'sendSms'
        ]),
        closeModal() {
            this.$emit('onClose', true);
        },
        handleChange() {
            this.isValid = true;
        },
        async handleSendSms() {
            if (this.tel.match(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im)) {
                this.loading = true;
                const params = {
                    phone: this.tel.includes('+') ? this.tel : '+' + this.tel,
                    from: null,
                    to: null
                };
                if(this.$route.name.includes('terminal')) {
                    params.from = this.getTerminalId;
                    params.to = parseInt(this.$route.params.id, 10);
                } else {
                    if(this.$route.query.from) {
                        params.from = parseInt(this.$route.query.from, 10);
                    } else {
                        params.from = this.getDenormalisedObjectById(this.t(this.getDenormalisedObjectById(parseInt(this.$route.query.to, 10)), 'closest_entrance')).id;
                        params.from_unit = this.getDenormalisedObjectById(this.t(this.getDenormalisedObjectById(parseInt(this.$route.query.to, 10)), 'closest_entrance')).unit.name;
                    }
                    params.to = parseInt(this.$route.query.to, 10);
                }
                try {
                    await this.sendSms(params);
                    this.status = 'success';
                } catch(error) {
                    this.status = 'fail';
                } finally {
                    this.loading = false;
                }
            } else {
                this.isValid = false;
            }
        }
    }
}
</script>

<style lang="scss">
@import "o-sms-modal";
</style>
